import { useEffect, useRef, useState } from "react";
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Text,
  VStack,
} from "@chakra-ui/react";

import { CreatorsApi, type BulkImportResult } from "../api/creators";

type Props = {
  isOpen: boolean;
  onClose: () => void;
  /** Called once after a successful import so the list page refreshes. */
  onImported: () => void;
};

const IMPORTABLE_STATUSES = ["PROSPECTIVE", "IMPORTED", "VOUCHED", "ACTIVE"] as const;
type ImportStatus = (typeof IMPORTABLE_STATUSES)[number];

export default function UploadCreatorsModal({
  isOpen,
  onClose,
  onImported,
}: Props) {
  const [origin, setOrigin] = useState<string>("");
  const [status, setStatus] = useState<ImportStatus>("IMPORTED");
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<BulkImportResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);

  // Reset all state when reopened.
  const [prevOpen, setPrevOpen] = useState(isOpen);
  if (isOpen !== prevOpen) {
    setPrevOpen(isOpen);
    if (isOpen) {
      setOrigin("");
      setStatus("IMPORTED");
      setFile(null);
      setResult(null);
      setError(null);
    }
  }

  // Clear the native file input so re-selecting the same file re-fires onChange.
  useEffect(() => {
    if (isOpen && fileInputRef.current) fileInputRef.current.value = "";
  }, [isOpen]);

  const canSubmit = !!file && !busy && !result;

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      const csv = await file.text();
      const r = await CreatorsApi.bulkImport({
        origin: origin.trim() || null,
        status,
        csv,
      });
      setResult(r);
      if (r.imported.length > 0 || r.updated.length > 0) onImported();
    } catch (e: unknown) {
      const msg =
        e && typeof e === "object" && "response" in e
          ? // axios error shape
            JSON.stringify(
              (e as { response?: { data?: unknown } }).response?.data ?? e,
            )
          : String(e);
      setError(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg" closeOnOverlayClick={!busy}>
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Upload creators from CSV</ModalHeader>
        <ModalCloseButton isDisabled={busy} />
        <ModalBody>
          {result ? (
            <ImportSummary result={result} />
          ) : (
            <VStack align="stretch" spacing={4}>
              <FormControl>
                <FormLabel>Origin</FormLabel>
                <Input
                  value={origin}
                  onChange={(e) => setOrigin(e.target.value)}
                  placeholder="optional"
                />
              </FormControl>
              <FormControl isRequired>
                <FormLabel>Status</FormLabel>
                <Select
                  value={status}
                  onChange={(e) => setStatus(e.target.value as ImportStatus)}
                >
                  {IMPORTABLE_STATUSES.map((s) => (
                    <option key={s} value={s}>
                      {s}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isRequired>
                <FormLabel>CSV file</FormLabel>
                <Input
                  ref={fileInputRef}
                  type="file"
                  accept=".csv,text/csv"
                  p={1}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
                <Text fontSize="xs" color="gray.600" mt={1}>
                  Headers (case-insensitive): <code>email</code> (required),{" "}
                  <code>name</code>, <code>type</code>, <code>link</code>,{" "}
                  <code>persona</code>, <code>description</code>. Max 1000 rows.
                </Text>
              </FormControl>
              {error && (
                <Alert status="error">
                  <AlertIcon />
                  <Box fontSize="sm">{error}</Box>
                </Alert>
              )}
            </VStack>
          )}
        </ModalBody>
        <ModalFooter>
          {result ? (
            <Button onClick={onClose}>Done</Button>
          ) : (
            <HStack>
              <Button variant="ghost" onClick={onClose} isDisabled={busy}>
                Cancel
              </Button>
              <Button
                colorScheme="blue"
                onClick={submit}
                isLoading={busy}
                isDisabled={!canSubmit}
              >
                Upload
              </Button>
            </HStack>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}

function ImportSummary({ result }: { result: BulkImportResult }) {
  return (
    <VStack align="stretch" spacing={3}>
      <Alert
        status={
          result.imported.length + result.updated.length > 0 ? "success" : "info"
        }
      >
        <AlertIcon />
        Imported {result.imported.length}, updated {result.updated.length},{" "}
        skipped {result.skipped.length}, errors {result.errors.length}.
      </Alert>
      {result.skipped.length > 0 && (
        <Box>
          <Text fontWeight="semibold" mb={1}>
            Skipped
          </Text>
          <Box as="ul" pl={5} fontSize="sm">
            {result.skipped.map((s, i) => (
              <li key={i}>
                row {s.row} ({s.email}) — {s.reason}
              </li>
            ))}
          </Box>
        </Box>
      )}
      {result.errors.length > 0 && (
        <Box>
          <Text fontWeight="semibold" mb={1}>
            Errors
          </Text>
          <Box as="ul" pl={5} fontSize="sm">
            {result.errors.map((e, i) => (
              <li key={i}>
                row {e.row}
                {e.email ? ` (${e.email})` : ""} — {e.reason}
              </li>
            ))}
          </Box>
        </Box>
      )}
    </VStack>
  );
}
